/* eslint-disable @typescript-eslint/no-explicit-any */
import type { GetContentOptions } from '@builder.io/sdk'
import { useCallback, useEffect, useState } from 'react'

import { getArticles } from '@/core/api/builder'
import { sendError } from '@/core/error'
import type { CardArticleEntryData } from '@/models'

export interface UseBlogListProps extends Pick<GetContentOptions, 'query' | 'fields'> {
  articles: CardArticleEntryData[]
  perPage: number
  sort?: { [key: string]: any }
}

export const useBlogList = ({ articles, query, sort, fields, perPage }: UseBlogListProps) => {
  const [state, setState] = useState({
    page: 0,
    hasMore: articles.length > perPage,
    loading: false,
    list: [...articles.slice(0, perPage)],
    firstNewPostIndex: 0,
  })

  useEffect(() => {
    setState((oldState) => ({
      ...oldState,
      page: 0,
      hasMore: articles.length > perPage,
      list: [...articles.slice(0, perPage)],
    }))
  }, [articles, perPage])

  const page = state.page

  const handleShowMore = useCallback(async () => {
    setState((oldState) => ({
      ...oldState,
      loading: true,
    }))

    try {
      const list = await getArticles({
        page: page + 1,
        fields,
        perPage,
        sort,
        query,
      })

      setState((oldState) => ({
        page: oldState.page + 1,
        list: [...oldState.list, ...(list.slice(0, perPage) || [])],
        hasMore: list.length > perPage,
        loading: false,
        firstNewPostIndex: oldState.list.length,
      }))
    } catch (error) {
      sendError({ error })
    }
  }, [perPage, query, sort, fields, page])

  return {
    state,
    handleShowMore,
  }
}
